import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RegionHierarchyInput } from '../components/RegionHierarchyInput';
import { useRegion } from '../hooks/useRegion';
import { useUpdateRegion } from '../hooks/useUpdateRegion';
import { updateRegionSchema, type UpdateRegionFormValues } from '../schemas/region-schema';
import { RegionSelector, RegionSelectorLabels } from '../types/regions.types';
import { CountryHierarchyMultiSelect } from '@/features/countries/components/CountryHierarchyMultiSelect';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface EditRegionModalProps {
  regionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditRegionModal({ regionId, open, onOpenChange }: EditRegionModalProps) {
  // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
  const { region, isLoading: isLoadingRegion } = useRegion(open ? regionId : undefined);
  const { updateRegion, isUpdating } = useUpdateRegion();
  const [pendingValues, setPendingValues] = useState<UpdateRegionFormValues | null>(null);

  const form = useForm<UpdateRegionFormValues>({
    resolver: zodResolver(updateRegionSchema),
    defaultValues: {
      name: '',
      selector: undefined,
      countryIds: [],
      parentId: null,
    },
  });

  const selectedCountryIds = form.watch('countryIds');
  const selectedSelector = form.watch('selector');
  const countryIdsKey = selectedCountryIds.join(',');

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
    if (open && region) {
      form.reset({
        name: region.name,
        selector: region.selector,
        countryIds: region.countryIds,
        parentId: region.parentId,
      });
    }
  }, [open, region, form]);

  const onSubmit = (values: UpdateRegionFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    updateRegion(
      { id: regionId, data: pendingValues },
      {
        onSuccess: () => {
          Notification.success({ title: 'Región actualizada exitosamente' });
          setPendingValues(null);
          handleClose();
        },
        onError: () => {
          setPendingValues(null);
        },
      },
    );
  };

  const handleClose = () => {
    if (!isUpdating) {
      form.reset();
      onOpenChange(false);
    }
  };

  const hasChildren = region?.children && region.children.length > 0;
  const getSelectorLabel = (value: number) =>
    RegionSelectorLabels[value as RegionSelector] ?? String(value);

  if (isLoadingRegion) {
    return (
      <ModalForm
        open={open}
        onOpenChange={handleClose}
        title="Editar región"
        className="sm:max-w-[425px]"
      >
        <div className="py-8" aria-hidden />
      </ModalForm>
    );
  }

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Globe />}
        onOpenChange={handleClose}
        title="Editar región"
        description="Modifica la información de la región seleccionada."
        className="sm:max-w-[425px]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUpdating}
                data-testid="cancel-edit-region-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isUpdating}
                data-testid="save-edit-region-btn"
              >
                Guardar
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre"
                  placeholder="Ej: Región Norte"
                  data-testid="edit-region-name-input"
                  disabled={isUpdating}
                  error={form.formState.errors.name?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="countryIds"
          render={({ field }) => (
            <FormItem data-testid="edit-region-country-select">
              <FormControl>
                <CountryHierarchyMultiSelect
                  label="País / Territorio"
                  placeholder="Seleccionar país"
                  value={field.value}
                  onValueChange={(ids) => {
                    field.onChange(ids);
                    form.setValue('parentId', null);
                  }}
                  disabled={!!hasChildren || isUpdating}
                />
              </FormControl>
              {hasChildren && (
                <p className="text-xs text-primary mt-1">
                  No editable: la región tiene dependencias (regiones hijas)
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="selector"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Tipo"
                  value={field.value?.toString() || ''}
                  onValueChange={(value) => {
                    field.onChange(Number(value));
                    // Radix Select fires a spurious onValueChange('') during its initial
                    // controlled-value sync — ignore it so it doesn't clobber a freshly
                    // loaded parentId before the user has actually touched this field.
                    if (value) {
                      form.setValue('parentId', null);
                    }
                  }}
                  disabled={!!hasChildren || isUpdating}
                  placeholder="Seleccionar tipo"
                  error={
                    hasChildren
                      ? 'No editable: la región tiene dependencias (regiones hijas)'
                      : form.formState.errors.selector?.message
                  }
                  data-testid="edit-region-selector-select"
                >
                  {Object.entries(RegionSelector).map(([key, value]) => (
                    <SelectItem key={key} value={value.toString()}>
                      {getSelectorLabel(value)}
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />

        {selectedCountryIds.length > 0 && selectedSelector !== undefined && (
          <FormField
            control={form.control}
            name="parentId"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <RegionHierarchyInput
                    key={`${countryIdsKey}-${selectedSelector}`}
                    countryIds={selectedCountryIds}
                    selector={selectedSelector as RegionSelector}
                    excludeId={regionId}
                    value={value}
                    onSelect={onChange}
                    label="Región padre (opcional)"
                    placeholder="Seleccionar región padre"
                    disabled={isUpdating}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isUpdating && !v && setPendingValues(null)}
        title="Confirmar cambios"
        description={
          <span>
            Se actualizará la región con nombre{' '}
            <span className="font-medium text-light">{pendingValues?.name}</span>.
          </span>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isUpdating}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
